import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Box, Link } from '@mui/material';
import MediaTypography from 'components/MediaTypography';
import store from 'store';

import MiroSrc from '@common/assets/images/miro.webp';
import { checkRole } from '@common/components/RBAC';
import { useServiceRouter } from '@common/components/ServiceRouter';
import { IClientGradeBookModel } from '@common/models/ClientGradeBook/ClientGradeBook';

import GradeBookLessonInfoTisModal from '../TisModal';
import {
	StyledGradeBookLessonArrowWrap,
	StyledGradeBookLessonInfo,
	StyledGradeBookLessonInfoNavigation,
	StyledGradeBookLessonMiroButton,
	StyledGradeBookLessonMiroImage,
	StyledGradeBookLessonOnlineText,
	StyledGradeBookLessonTisImage,
} from './styled';

interface PropsType {
	title: string;
	subTitle: string;
	isCollapsed?: boolean;
	link?: string;
	miroLink?: string;
	gradeBook: IClientGradeBookModel;
}

function GradeBookLessonInfo({
	title,
	subTitle,
	isCollapsed = true,
	link,
	miroLink,
	gradeBook,
}: PropsType) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const { checkService } = useServiceRouter();

	if (!title) {
		return null;
	}

	const isProgramTis = !!gradeBook.schedule_lesson?.schedule_program?.program_group?.program?.isTis;
	const isLyceum = checkService('lyceum');
	const hasUserTis = store.profile.services.some((service) => service.id === 3);

	const showTis = isProgramTis && isLyceum && hasUserTis;
	const isHomeTaskEnabled = !!gradeBook.schedule_lesson?.toHomework;

	return (
		<StyledGradeBookLessonInfo
			showTis={showTis}
			sx={(theme) => ({
				...(!isCollapsed && {
					background: theme.palette.primary.main,
					color: theme.palette.text.white,
				}),
			})}
		>
			{showTis && (
				<>
					<GradeBookLessonInfoTisModal open={isOpen} onClose={() => setIsOpen(false)} />
					<StyledGradeBookLessonTisImage onClick={() => setIsOpen(true)} />
				</>
			)}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
					gap: '24px',
				}}
			>
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<MediaTypography variant="body1" color="#1F1F1F">
							{title}
						</MediaTypography>
						{isHomeTaskEnabled && (
							<MediaTypography color="#3274D5" variant="body2" sx={{ textDecoration: 'underline' }}>
								{t('gradeBook.ht')}
							</MediaTypography>
						)}
					</Box>
					<MediaTypography variant="body2" color="#858585">
						{subTitle}
					</MediaTypography>
				</Box>
				<Box>
					<StyledGradeBookLessonInfoNavigation
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						{!!link && !checkRole('Parent') && (
							<Link href={link} target="_blank">
								<StyledGradeBookLessonArrowWrap color="info" variant="contained">
									<StyledGradeBookLessonOnlineText variant="body2">
										{t('gradeBook.join')}
									</StyledGradeBookLessonOnlineText>
									<ArrowOutwardIcon
										sx={(theme) => ({ fontSize: '16px', color: theme.palette.info.contrastText })}
									/>
								</StyledGradeBookLessonArrowWrap>
							</Link>
						)}
						{miroLink && (
							<Link href={miroLink} target="_blank">
								<StyledGradeBookLessonMiroButton>
									<StyledGradeBookLessonMiroImage src={MiroSrc} alt="Miro" />
								</StyledGradeBookLessonMiroButton>
							</Link>
						)}
					</StyledGradeBookLessonInfoNavigation>
				</Box>
			</Box>
		</StyledGradeBookLessonInfo>
	);
}

export default GradeBookLessonInfo;
